import { Helmet } from 'react-helmet-async'
import { Download } from 'lucide-react'
import { trackDownloadClick } from '@/lib/trackDownload'
import { useAuth } from '@/contexts/auth-context'
import { useAuthActions } from '@convex-dev/auth/react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { isElectron } from '@/lib/electron'

import { Navbar } from '@/components/homepage/sections/Navbar'
import { HomepageFooter } from '@/components/homepage/sections/HomepageFooter'
import { CornerPlus } from '@/components/ui/corner-plus'
import { OG_IMAGE_URL, cdnUrl, siteUrl } from '@/lib/config/site'

const DOWNLOAD_BASE_URL = cdnUrl('/downloads')

const MAC_DOWNLOADS = [
  {
    name: 'Apple Silicon',
    description: 'For Apple Silicon Macs',
    filename: 'Footage-1.0.7-arm64.dmg',
  },
  {
    name: 'Intel',
    description: 'For older Intel-based Macs',
    filename: 'Footage-1.0.7.dmg',
  },
]

export default function DownloadMacPage() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const { signIn } = useAuthActions()
  const [, setIsLoading] = useState(false)

  const handleSignUp = () => {
    if (isElectron()) {
      navigate('/signup')
      return
    }
    setIsLoading(true)
    signIn('google', { redirectTo: `${window.location.origin}/auth` })
  }

  return (
    <div className="min-h-screen bg-background px-4 lg:px-0 overflow-x-hidden">
      <Helmet>
        <title>Download Footage for Mac</title>
        <meta name="description" content="Download Footage for macOS. Apple Silicon and Intel supported." />
        <link rel="canonical" href={siteUrl('/download-for-mac')} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Download Footage for Mac" />
        <meta property="og:description" content="Download Footage for macOS. Apple Silicon and Intel supported." />
        <meta property="og:url" content={siteUrl('/download-for-mac')} />
        <meta property="og:image" content={OG_IMAGE_URL} />
        <meta property="og:site_name" content="Footage" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Download Footage for Mac" />
        <meta name="twitter:description" content="Download Footage for macOS. Apple Silicon and Intel supported." />
        <meta name="twitter:image" content={OG_IMAGE_URL} />
      </Helmet>

      <div className="max-w-7xl mx-auto border-x border-border">
        <Navbar isAuthenticated={isAuthenticated} onSignUp={handleSignUp} />
        <main className="flex flex-col divide-y divide-border pt-16">
          {/* Hero */}
          <section className="relative flex flex-col items-center justify-center px-4 py-24 md:py-36">
            <CornerPlus position="bottom-left" className="text-muted-foreground/50" />
            <CornerPlus position="bottom-right" className="text-muted-foreground/50" />
            <div className="absolute inset-0 z-0 h-full w-full bg-radial-[at_45%_85%] from-[#2CD5FF]/40 via-[#2C30FF]/4 mask-[linear-gradient(to_bottom,transparent,black_100%)]" />
            <div className="absolute inset-0 z-0 h-full w-full bg-radial-[at_45%_68%] from-[#2CD5FF]/68 via-[#2C30FF]/3 mask-[linear-gradient(to_bottom,transparent,black_100%)] blur-[50px]" />
            <div className="relative z-10 flex flex-col items-center justify-center text-center space-y-6 max-w-4xl mx-auto">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter text-balance">
                Download for Mac
              </h1>
              <p className="text-secondary-foreground/70 text-center text-balance text-lg max-w-2xl mx-auto">
                Create stunning content with AI. Host, share, and publish your content — all from your desktop.
              </p>
            </div>
          </section>

          {/* Download options */}
          <section className="w-full relative">
            <div className="mx-auto">
              <div className="grid md:grid-cols-6 lg:divide-x divide-border">
                <div className="col-span-2 flex flex-col gap-4 p-8 md:p-12">
                  <h2 className="text-3xl md:text-4xl font-medium tracking-tighter text-left text-balance">
                    Footage for macOS
                  </h2>
                  <p className="text-muted-foreground text-left text-balance">
                    Requires macOS 10.15 or later
                  </p>
                </div>

                <div className="col-span-4 w-full p-8 md:p-12">
                  <div className="space-y-4 max-w-lg">
                    {MAC_DOWNLOADS.map((variant) => (
                      <a
                        key={variant.filename}
                        href={`${DOWNLOAD_BASE_URL}/${variant.filename}`}
                        onClick={() => trackDownloadClick('download-page', variant.name)}
                        className="flex items-center justify-between gap-4 px-6 py-5 rounded-2xl bg-foreground text-primary-foreground hover:bg-foreground/90 transition-all"
                      >
                        <div>
                          <div className="font-semibold text-lg">
                            {variant.name}
                          </div>
                          <div className="text-sm text-primary-foreground/70">
                            {variant.description}
                          </div>
                        </div>
                        <div className="p-3 rounded-xl bg-white/10">
                          <Download className="h-5 w-5" />
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <HomepageFooter />
        </main>
      </div>
    </div>
  )
}
